/** Uzamsal ve hız: labirent, zihinsel döndürme, ayna, aritmetik, zıt yönerge */

import { Rng } from '../../prng'
import { ARROWS, carveMaze, gradeList, gradeSingle, solveMaze } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { CellNode, ExerciseTemplate } from '../types'
import type { ShapeName } from '../../render/shapes'

/* ================================================================== *
 * 16. Labirent
 *
 * Üretici çözümü BULMADAN sayfayı teslim etmez. Çözülemeyen bir
 * labirent basmak, bir egzersiz programının yapabileceği en kötü şey:
 * çocuk kendi beceriksizliğine yorar.
 * ================================================================== */
export const maze: ExerciseTemplate = {
  code: 'maze',
  title: 'Labirent',
  domains: ['working_memory_visual', 'selective_attention'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { cols: scale(l, 6, 16), rows: scale(l, 6, 16), extraOpenings: scale(l, 8, 0) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const cols = p['cols'] as number
    const rows = p['rows'] as number

    let m = carveMaze(new Rng(seed), cols, rows, p['extraOpenings'] as number)
    let path = solveMaze(m)

    // Teorik olarak her zaman çözülebilir; yine de kontrol ediyoruz.
    // "Garanti olmalı" ile "garanti" arasındaki fark bir hata ayıklama
    // gecesine mal olur.
    let guard = 0
    while (path === null && guard++ < 5) {
      m = carveMaze(new Rng(Number(seed) + guard * 7919), cols, rows, 0)
      path = solveMaze(m)
    }
    if (path === null) throw new Error('maze: cozulebilir labirent uretilemedi')

    const cells: CellNode[] = m.walls.map((w, i) => ({
      walls: w,
      id: 'm' + i,
      marked: i === m.start || i === m.end,
      glyph: i === m.start ? 'A' : i === m.end ? 'B' : undefined,
    }))

    return {
      code: 'maze',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'A noktasından B noktasına giden yolu çiz.',
      render: [{ t: 'grid', cols, rows, cells, border: true }],
      solution: path,
      grading: 'auto',
      estimatedSeconds: Math.round(path.length * 2.5),
      itemCount: 1,
    }
  },
  grade(instance, answer) {
    const want = instance.solution as number[]
    const got = Array.isArray(answer) ? answer.map(Number) : []
    // Yol doğru sayılır: başlangıç ve bitiş doğruysa ve adımlar komşuysa.
    // Aynı uzunlukta olması şart değil — ek açıklıklı labirentte birden
    // fazla geçerli yol olabilir.
    const ok =
      got.length > 1 && got[0] === want[0] && got[got.length - 1] === want[want.length - 1]
    return {
      correct: ok ? 1 : 0,
      total: 1,
      accuracy: ok ? 1 : 0,
      perItem: [ok],
      items: [
        {
          id: 'yol',
          beklenen: `${want[0]} → ${want[want.length - 1]} (${want.length} adım)`,
          verilen: got.length ? `${got[0]} → ${got[got.length - 1]} (${got.length} adım)` : null,
          dogru: ok,
        },
      ],
    }
  },
}

/* ================================================================== *
 * 17. Zihinsel döndürme
 *
 * Verilen şeklin döndürülmüş hali hangisi.
 *
 * ESKİ HALİ BOZUKTU ve bozukluğu sessizdi: şablon bir açı hesaplıyor
 * (`rot: angle`) ama `choices` düğümünün dönme alanı yok, açı çizim
 * ağacına hiç ulaşmıyordu. Ekranda hiçbir şey dönmüyordu; doğru şık
 * 300 tohumun 300'ünde temel şeklin BİREBİR AYNISI, çeldiriciler ise
 * başka şekillerdi. Yani yönerge "döndürülmüş hali hangisi" diyordu,
 * görev ise "aynısını bul" idi — her seviyede aynı ve her seviyede
 * kolay. `angle` ve `mirrorTrap` parametreleri hiçbir işe yaramıyordu.
 *
 * Şekiller de uygun değildi: `◣ ◤ ◥ ◢` birbirinin 90° dönmüş hali,
 * `⬓ ⬔` de öyle. Aynı aileden çeldirici seçmek, dönme gerçekten
 * çizilseydi bu kez doğru şıkla aynı görünen çeldiriciler üretirdi.
 *
 * Yeni kurgu, klasik Shepard–Metzler mantığı:
 *   doğru şık   = şeklin kendisi, 90/180/270 döndürülmüş
 *   çeldiriciler = şeklin AYNASI, çeşitli açılarda döndürülmüş
 * Şekil kiral seçiliyor (aynası hiçbir dönmesine eşit değil), bu yüzden
 * bir çeldirici doğru şıkla asla aynı görünemiyor. Düşük seviyede
 * çeldiriciler başka şekiller — ayna tuzağı en zor çeldirici tipi,
 * onu 4. seviyeden sonra devreye alıyoruz.
 *
 * Çizim metin glifi değil, hücre matrisi (`tiles`). Dönme matris
 * üzerinde yapılıyor; glifi eğerek döndürmek sorunu yazı tipine
 * taşırdı. Matrisin yan etkisi, dönmenin yalnızca 90°'nin katı
 * olabilmesi — eski koddaki 97°–150° ara açılar zaten doğru şıkkı
 * "tek eğik duran" yapıp görevi bakmakla çözülür hale getiriyordu.
 * ================================================================== */

/** Kare matrisi saat yönünde 90° döndürür */
function dondur(on: boolean[], n: number): boolean[] {
  const out = new Array<boolean>(n * n).fill(false)
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) out[c * n + (n - 1 - r)] = on[r * n + c]!
  }
  return out
}

/** Dikey eksende aynalar */
function aynala(on: boolean[], n: number): boolean[] {
  const out = new Array<boolean>(n * n).fill(false)
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) out[r * n + (n - 1 - c)] = on[r * n + c]!
  }
  return out
}

function dortDonme(on: boolean[], n: number): boolean[][] {
  const a = on
  const b = dondur(a, n)
  const c = dondur(b, n)
  const d = dondur(c, n)
  return [a, b, c, d]
}

const esit = (a: boolean[], b: boolean[]) => a.length === b.length && a.every((x, i) => x === b[i])

/** Şeklin herhangi bir dönmesi öbürüne eşit mi */
const donmeEsi = (a: boolean[], b: boolean[], n: number) => dortDonme(a, n).some((d) => esit(d, b))

/**
 * Bağlı (hücreleri birbirine komşu) rastgele şekil.
 *
 * Bağlılık şart: dağınık hücreler bir "şekil" değil, gürültü. Çocuk
 * gürültüyü zihninde çeviremiyor, tek tek hücre sayıyor — ölçtüğümüz
 * şey uzamsal dönüşüm olmaktan çıkıyor.
 */
function sekilUret(rng: Rng, n: number, hucre: number): boolean[] {
  const on = new Array<boolean>(n * n).fill(false)
  let r = rng.int(0, n - 1)
  let c = rng.int(0, n - 1)
  on[r * n + c] = true
  let kalan = hucre - 1
  let deneme = 0
  while (kalan > 0 && deneme < 200) {
    deneme++
    const dolular: number[] = []
    for (let i = 0; i < n * n; i++) if (on[i]) dolular.push(i)
    const k = rng.pick(dolular)
    r = Math.floor(k / n)
    c = k % n
    const [dr, dc] = rng.pick([
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ] as const)
    const nr = r + dr
    const nc = c + dc
    if (nr < 0 || nr >= n || nc < 0 || nc >= n) continue
    if (on[nr * n + nc]) continue
    on[nr * n + nc] = true
    kalan--
  }
  return on
}

/** Aynası hiçbir dönmesine eşit olmayan (kiral) şekil üretene kadar dener */
function kiralSekil(rng: Rng, n: number, hucre: number): boolean[] {
  for (let i = 0; i < 60; i++) {
    const s = sekilUret(rng, n, hucre)
    if (!donmeEsi(s, aynala(s, n), n)) return s
  }
  // 60 denemede bulunamazsa L biçimi: her boyutta kiraldir
  const on = new Array<boolean>(n * n).fill(false)
  for (let r = 0; r < n; r++) on[r * n] = true
  on[(n - 1) * n + 1] = true
  return on
}

export const mentalRotation: ExerciseTemplate = {
  code: 'mental_rotation',
  title: 'Döndürülmüş şekli bul',
  domains: ['working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Izgara kenarı: şekil büyüdükçe dönmeyi izlemek zorlaşıyor */
      n: scale(l, 3, 5),
      /** Dolu hücre sayısı */
      cells: scale(l, 3, 8),
      choices: scale(l, 3, 6),
      /**
       * Ayna çeldiricisi — en zor tip, 4. seviyeden sonra.
       *
       * "Üst seviyede TÜM çeldiriciler ayna olsun" diye bir kademe
       * DENENDİ ve kaldırıldı: bir şeklin aynasının yalnızca dört
       * dönmesi var, 10. seviyede ise beş çeldirici gerekiyor. Geometri
       * o kademeyi kaldırmıyor. Zorluk ızgara boyu, hücre sayısı, şık
       * sayısı ve ayna tuzağının varlığından geliyor; dördü yeter.
       */
      mirrorTrap: l >= 4,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const n = p['n'] as number
    const hucre = Math.min(p['cells'] as number, n * n - 1)
    const choiceCount = p['choices'] as number

    const aynaTuzak = p['mirrorTrap'] as boolean

    /**
     * Kiral şekil yalnızca ayna tuzağı kullanıldığında ŞART.
     *
     * Aynası kendi dönmelerinden birine eşit olan şekilde ayna
     * çeldiricisi doğru şıkla aynı görünür — belirsiz madde. Ama alt
     * seviyelerde ayna kullanmıyoruz ve orada kiral şekil zaten
     * OLMAYABİLİR: 3×3'te 3 hücreli bağlı şekiller I ve L; ikisinin de
     * aynası kendi dönmelerinden biri, yani 3 hücreli kiral şekil yok.
     * Kiralliği her seviyede şart koşmak, o seviyelerde üreteci sabit
     * bir yedek şekle düşürüyordu ve "başka şekil" çeldiricisi hiç
     * bulunamadığı için döngü kapanmıyordu.
     */
    /**
     * Şekil, dönme bakımından SİMETRİK OLMAYACAK.
     *
     * Yoksa düzeltmeye çalıştığımız hata küçük ölçekte geri geliyor:
     * 3×3'ün orta sütununa dizilmiş üç hücre 180° döndürülünce kendisi
     * oluyor, o dönme doğru şık seçilirse cevap yine temel şeklin
     * aynısı çıkıyor. 300 tohumda 7 kez oluyordu.
     */
    let temel = aynaTuzak ? kiralSekil(rng, n, hucre) : sekilUret(rng, n, hucre)
    let farkliDonmeler = dortDonme(temel, n)
      .slice(1)
      .filter((d) => !esit(d, temel))
    for (let i = 0; i < 40 && farkliDonmeler.length === 0; i++) {
      temel = aynaTuzak ? kiralSekil(rng, n, hucre) : sekilUret(rng, n, hucre)
      farkliDonmeler = dortDonme(temel, n)
        .slice(1)
        .filter((d) => !esit(d, temel))
    }

    // Doğru şık: temelden GERÇEKTEN farklı bir dönme. 0° yok, ve
    // şeklin kendisine eşit düşen dönmeler de yok.
    const dogru = farkliDonmeler.length > 0 ? rng.pick(farkliDonmeler) : dortDonme(temel, n)[1]!

    /**
     * Çeldirici havuzu, zorluk sırasına göre doldurulup baştan alınıyor.
     * Döngülerin hepsi SINIRLI: yeterli çeldirici bulunamazsa şık sayısı
     * eksik kalır ve sınama bunu yakalar — üreteç asla kilitlenmez.
     */
    const havuz: boolean[][] = []
    const ekle = (d: boolean[]) => {
      // Temelin bir dönmesi çeldirici olamaz: doğru şıkla aynı görünür
      if (donmeEsi(temel, d, n)) return
      if (havuz.some((x) => esit(x, d))) return
      havuz.push(d)
    }

    // 1) En zor tuzak: temelin aynasının dönmeleri
    if (aynaTuzak) for (const d of dortDonme(aynala(temel, n), n)) ekle(d)

    // 2) Aynı hücre sayılı başka şekiller
    for (let i = 0; i < 40 && havuz.length < choiceCount - 1; i++) {
      for (const d of dortDonme(sekilUret(rng, n, hucre), n)) ekle(d)
    }

    // 3) Son çare: hücre sayısı farklı şekil. Dönme hücre sayısını
    // değiştirmediği için bunlar tanım gereği geçerli çeldirici — ama
    // sayarak ayırt edilebildikleri için zayıf, o yüzden en sonda.
    for (let fark = 1; fark <= 2 && havuz.length < choiceCount - 1; fark++) {
      for (const h of [hucre + fark, hucre - fark]) {
        if (h < 2 || h > n * n - 1) continue
        for (const d of dortDonme(sekilUret(rng, n, h), n)) ekle(d)
      }
    }

    const celdiriciler = rng.shuffle(havuz).slice(0, choiceCount - 1)

    const siralı = rng.shuffle([
      { on: dogru, correct: true },
      ...celdiriciler.map((on) => ({ on, correct: false })),
    ])
    const items = siralı.map((o, i) => ({ id: 'o' + i, on: o.on }))

    return {
      code: 'mental_rotation',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Yukarıdaki şeklin döndürülmüş hali hangisi?',
      render: [
        { t: 'tiles', n, items: [{ id: 'temel', on: temel }] },
        { t: 'text', value: 'Seçenekler:', size: 'sm' },
        { t: 'tiles', n, items, pick: true },
      ],
      solution: 'o' + siralı.findIndex((o) => o.correct),
      grading: 'auto',
      estimatedSeconds: 30,
      itemCount: 1,
    }
  },
  grade(instance, answer) {
    return gradeSingle(instance.solution, answer)
  },
}

/* ================================================================== *
 * 18. Ayna görüntüsü
 * ================================================================== */
export const mirrorImage: ExerciseTemplate = {
  code: 'mirror_image',
  title: 'Aynadaki hali çiz',
  domains: ['working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { size: scale(l, 4, 8), filled: scale(l, 4, 14) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const size = p['size'] as number
    const half = Math.floor(size / 2)
    const n = size * size

    const left: CellNode[] = Array.from({ length: n }, (_, i) => ({ id: 'L' + i }))
    const filledCount = Math.min(p['filled'] as number, half * size)
    const slots = rng.sample(
      Array.from({ length: half * size }, (_, i) => i),
      filledCount,
    )
    const solution: string[] = []
    for (const s of slots) {
      const r = Math.floor(s / half)
      const c = s % half
      left[r * size + c] = { shape: 'kare', id: 'L' + (r * size + c) }
      // Ayna karşılığı
      const mc = size - 1 - c
      solution.push('L' + (r * size + mc))
    }

    return {
      code: 'mirror_image',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Sol yarıdaki şeklin aynadaki görüntüsünü sağ yarıya çiz.',
      render: [{ t: 'grid', cols: size, rows: size, cells: left, border: true }],
      solution,
      grading: 'auto',
      estimatedSeconds: filledCount * 10,
      itemCount: filledCount,
    }
  },
  grade(instance, answer) {
    const want = instance.solution as string[]
    const got = new Set((Array.isArray(answer) ? answer : []).map(String))
    const correct = want.filter((w) => got.has(w)).length
    const extra = [...got].filter((g) => !want.includes(g)).length
    const net = Math.max(0, correct - extra)
    return {
      correct: net,
      total: want.length,
      accuracy: want.length ? net / want.length : 0,
      perItem: want.map((w) => got.has(w)),
      items: [
        ...want.map((w) => ({
          id: w,
          beklenen: 'işaretlenmeli',
          verilen: got.has(w) ? 'işaretlendi' : null,
          dogru: got.has(w),
        })),
        ...[...got]
          .filter((g) => !want.includes(g))
          .map((g) => ({
            id: g,
            beklenen: 'işaretlenmemeli',
            verilen: 'işaretlendi',
            dogru: false,
            fazladan: true,
          })),
      ],
    }
  },
}

/* ================================================================== *
 * 19. Aritmetik akıcılık
 *
 * İşlem hızı. Taşımalı/taşımasız ayrımı önemli: taşımasız toplama
 * otomatikleşir, taşımalı işleyen bellek ister.
 * ================================================================== */
export const arithFluency: ExerciseTemplate = {
  code: 'arith_fluency',
  title: 'Hızlı işlem',
  domains: ['processing_speed', 'working_memory_verbal'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      items: scale(l, 15, 50),
      maxOperand: scale(l, 9, 99),
      carry: l >= 4,
      ops: l >= 7 ? 4 : l >= 3 ? 2 : 1,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const count = p['items'] as number
    const max = p['maxOperand'] as number
    const opCount = p['ops'] as number
    const allowCarry = p['carry'] as boolean

    const rows: { label: string; answer: string }[] = []
    for (let i = 0; i < count; i++) {
      const op = rng.int(0, opCount - 1)
      let a = rng.int(1, max)
      let b = rng.int(1, max)
      let label: string
      let answer: number

      if (op === 0) {
        if (!allowCarry) {
          // Taşımasız toplama: basamakları ayrı ayrı kurup toplamlarını
          // 10'un altında tutuyoruz.
          //
          // Önceki hali "rastgele seç, taşıma varsa tekrar dene" idi ve
          // seviye 1'de (sayılar 1–9) a=9 geldiğinde hiçbir b koşulu
          // sağlamadığı için SONSUZ DÖNGÜYE giriyordu. Reddet-ve-tekrar-dene
          // kalıbı, çözüm kümesinin boş olabildiği yerde kullanılmaz.
          const tensMax = Math.floor(Math.min(max, 44) / 10)
          const au = rng.int(1, 8)
          const bu = rng.int(1, 9 - au)
          const at = tensMax > 0 ? rng.int(0, tensMax) : 0
          const bt = tensMax > 0 ? rng.int(0, Math.max(0, tensMax - at)) : 0
          a = at * 10 + au
          b = bt * 10 + bu
        }
        label = `${a} + ${b} =`
        answer = a + b
      } else if (op === 1) {
        if (b > a) [a, b] = [b, a]
        label = `${a} − ${b} =`
        answer = a - b
      } else if (op === 2) {
        a = rng.int(2, 9)
        b = rng.int(2, 9)
        label = `${a} × ${b} =`
        answer = a * b
      } else {
        b = rng.int(2, 9)
        answer = rng.int(2, 9)
        a = b * answer
        label = `${a} ÷ ${b} =`
      }
      rows.push({ label, answer: String(answer) })
    }

    return {
      code: 'arith_fluency',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'İşlemleri olabildiğince hızlı ve doğru yap.',
      render: [
        { t: 'rows', items: rows.map((r, i) => ({ label: r.label, blank: true, id: 'a' + i })), columns: 4 },
      ],
      solution: rows.map((r) => r.answer),
      grading: 'auto',
      estimatedSeconds: count * 5,
      itemCount: count,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 20. Zıt yönerge
 *
 * "Ok yukarı ise aşağı yaz." Ketleme egzersizi: otomatik cevabı
 * bastırıp kuralı uygulamak gerekir. Yüksek seviyede kural satır
 * ortasında değişir.
 * ================================================================== */
export const oppositeRule: ExerciseTemplate = {
  code: 'opposite_rule',
  title: 'Tersini yaz',
  domains: ['inhibition', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { items: scale(l, 12, 40), switchRule: l >= 6, diagonal: l >= 8 }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const count = p['items'] as number
    const pool = p['diagonal'] ? ARROWS : ARROWS.slice(0, 4)
    /**
     * Her okun 180° karşılığı.
     *
     * Açıdan türetilebilirdi (`donme + 180`) ama tablo açıkça duruyor:
     * bir okun tersinin ne olduğu bu egzersizin TANIMI, çizim
     * ayrıntısından türetilecek bir şey değil.
     */
    const opposite: Record<string, ShapeName> = {
      ok_yukari: 'ok_asagi',
      ok_asagi: 'ok_yukari',
      ok_sag: 'ok_sol',
      ok_sol: 'ok_sag',
      ok_sol_yukari: 'ok_sag_asagi',
      ok_sag_asagi: 'ok_sol_yukari',
      ok_sag_yukari: 'ok_sol_asagi',
      ok_sol_asagi: 'ok_sag_yukari',
    }

    const items: { shape: ShapeName; answer: ShapeName }[] = []
    const switchAt = p['switchRule'] ? Math.floor(count / 2) : count
    for (let i = 0; i < count; i++) {
      const g = rng.pick(pool)
      // İlk yarıda tersini yaz, ikinci yarıda aynısını — kural ortada değişir
      const same = i >= switchAt
      items.push({ shape: g, answer: same ? g : opposite[g]! })
    }

    const promptParts = ['Her okun TERSİNİ yaz.']
    if (p['switchRule']) promptParts.push('Çizgiden sonra okun AYNISINI yaz.')

    return {
      code: 'opposite_rule',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: promptParts.join(' '),
      render: [
        {
          t: 'rows',
          items: items.map((it, i) => ({ label: '', labelShape: it.shape, blank: true, id: 'o' + i })),
          columns: 8,
          // Ok işaretleri klavyede yok: cevap paletten dokunarak veriliyor
          palette: pool.map((o) => ({ shape: o })),
        },
      ],
      solution: items.map((i) => i.answer),
      grading: 'auto',
      estimatedSeconds: count * 4,
      itemCount: count,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}
